import { Modal } from './ModalHost';
import {
  ApiError,
  getActivationBlockers,
  type ActivationBlocker,
} from '@/types/errors';
import { formatCurrencyString } from '@/utils/format';

interface Props {
  error: ApiError;
  onClose: () => void;
}

export function ActivationBlockersModal({ error, onClose }: Props) {
  // Either composite (`activation_blocked` with details.blockers[]) or
  // single-blocker (`incomplete_sales_credit` / `payment_schedule_mismatch`
  // / `no_assignments`).
  let blockers: ActivationBlocker[];
  if (error.code === 'activation_blocked') {
    blockers = getActivationBlockers(error);
  } else {
    blockers = [
      {
        code: error.code as ActivationBlocker['code'],
        details: error.details,
      },
    ];
  }
  return (
    <Modal
      open={true}
      onClose={onClose}
      title="Activation requirements not met"
      role="alertdialog"
      scrimDismissDisabled
    >
      <ul style={{ paddingLeft: 16 }}>
        {blockers.map((b, i) => (
          <li key={`${b.code}-${i}`} style={{ marginBottom: 12 }}>
            <BlockerLine blocker={b} />
          </li>
        ))}
      </ul>
      <div className="form-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </Modal>
  );
}

function BlockerLine({ blocker }: { blocker: ActivationBlocker }) {
  if (blocker.code === 'no_assignments') {
    return (
      <span>
        Add at least one assignment.
      </span>
    );
  }
  if (blocker.code === 'incomplete_sales_credit') {
    const blocked = blocker.details['blocked_assignments'];
    const list = Array.isArray(blocked) ? blocked : [];
    return (
      <div>
        <strong>Sales Credit is not yet 100% on every assignment.</strong>
        <ul style={{ marginTop: 4 }}>
          {list.map((row, idx) => {
            const r = row as { assignment_id?: string; sales_total?: string };
            return (
              <li key={r.assignment_id ?? idx}>
                Assignment {r.assignment_id ?? '?'}: Sales total {r.sales_total ?? '0'}%
              </li>
            );
          })}
        </ul>
      </div>
    );
  }
  if (blocker.code === 'payment_schedule_mismatch') {
    const total = String(blocker.details['total_value'] ?? '');
    const sched = String(blocker.details['schedule_total'] ?? '');
    const diff = String(blocker.details['difference'] ?? '');
    return (
      <span>
        Payment schedule total {formatCurrencyString(sched)} is {formatCurrencyString(diff)} away
        from Total Value {formatCurrencyString(total)}. Adjust to within $100 to activate.
      </span>
    );
  }
  return <span>{blocker.code}</span>;
}
